import type { ReactNode } from 'react';
import { conversionNote, money } from '@gdec/shared';
import { computeRow, draftTotals, type DraftRow } from './draft';

export interface DraftTableProps {
  rows: readonly DraftRow[];
  categories: readonly string[];
  /** Duplicate-receipt warnings, keyed by row id. */
  warnings: Readonly<Record<string, string>>;
  onField: (id: string, patch: Partial<DraftRow>) => void;
  /** Dates are checked against the claim period the moment they are picked. */
  onDateCommit: (id: string, value: string) => void;
  /** Receipt numbers are checked on blur, against the draft and the server. */
  onReceiptCommit: (id: string, value: string) => void;
  onDelete: (id: string) => void;
}

const COLUMN_COUNT = 11;

/**
 * Category options for one row.
 *
 * A claim loaded for resubmission can hold a category that has since been
 * renamed or deactivated. That text is the historical record, so it is kept as
 * an option rather than silently rewritten to the first active category.
 */
function optionsFor(categories: readonly string[], current: string): string[] {
  if (!current || categories.includes(current)) return [...categories];
  return [current, ...categories];
}

export function DraftTable({
  rows,
  categories,
  warnings,
  onField,
  onDateCommit,
  onReceiptCommit,
  onDelete,
}: DraftTableProps): ReactNode {
  const totals = draftTotals(rows);

  return (
    <div className="table-scroll">
      <table className="cf-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Receipt No.</th>
            <th>Company Name</th>
            <th>TIN Number</th>
            <th>Description</th>
            <th>Amount</th>
            <th>VAT?</th>
            <th>VAT</th>
            <th>Net of VAT</th>
            <th>Total</th>
            <th>
              <span className="sr-only">Remove row</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="cf-empty-row">
              <td colSpan={COLUMN_COUNT}>
                No expenses yet — upload a receipt or add a row manually to get started.
              </td>
            </tr>
          ) : null}

          {rows.map((row, index) => {
            const { vat, net, total } = computeRow(row);
            const line = index + 1;
            const warning = warnings[row.id];
            return (
              <tr key={row.id}>
                <td>
                  <input
                    type="date"
                    value={row.date}
                    aria-label={`Date, row ${line}`}
                    onChange={(e) => onDateCommit(row.id, e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={row.receipt}
                    aria-label={`Receipt number, row ${line}`}
                    aria-invalid={warning ? true : undefined}
                    onChange={(e) => onField(row.id, { receipt: e.target.value })}
                    onBlur={(e) => onReceiptCommit(row.id, e.target.value)}
                  />
                  {row.sourceName ? (
                    <div className="cf-cell-note" title={row.sourceName}>
                      📎 {row.sourceName}
                    </div>
                  ) : null}
                  {warning ? <div className="cf-cell-warn">{warning}</div> : null}
                </td>
                <td className="tl">
                  <input
                    type="text"
                    value={row.company}
                    aria-label={`Company name, row ${line}`}
                    onChange={(e) => onField(row.id, { company: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={row.tin}
                    aria-label={`TIN number, row ${line}`}
                    onChange={(e) => onField(row.id, { tin: e.target.value })}
                  />
                </td>
                <td className="tl">
                  <select
                    value={row.description}
                    aria-label={`Description, row ${line}`}
                    onChange={(e) => onField(row.id, { description: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {optionsFor(categories, row.description).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={row.amount}
                    aria-label={`Amount, row ${line}`}
                    onChange={(e) => onField(row.id, { amount: e.target.value })}
                  />
                  {/* The rate is printed on the row itself, not tucked into a
                      tooltip. Finance approving a converted claim has to see
                      what it was converted at without asking anyone. */}
                  {conversionNote(Number(row.originalAmount), row.currency, row.fxRate) ? (
                    <span className="cf-fx">
                      {conversionNote(Number(row.originalAmount), row.currency, row.fxRate)}
                    </span>
                  ) : null}
                </td>
                <td>
                  <select
                    className={row.vatStatus === 'Yes' ? 'cf-vat-yes' : 'cf-vat-no'}
                    value={row.vatStatus}
                    aria-label={`VAT registered, row ${line}`}
                    onChange={(e) =>
                      onField(row.id, { vatStatus: e.target.value === 'Yes' ? 'Yes' : 'No' })
                    }
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </td>
                <td className="ro">{money(vat)}</td>
                <td className="ro">{money(net)}</td>
                <td className="ro">{money(total)}</td>
                <td className="cf-del-cell">
                  <button
                    type="button"
                    onClick={() => onDelete(row.id)}
                    aria-label={`Remove row ${line}`}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="tl2">
              TOTAL CLAIMED AMOUNT
            </td>
            <td>{money(totals.total)}</td>
            <td />
            <td>{money(totals.vat)}</td>
            <td>{money(totals.net)}</td>
            <td>{money(totals.total)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
